import { isBreak, type PointEvent } from "./types";

/**
 * What a finished game's point list can say beyond the score (board
 * redesign, 2026-09-10): the worm, the runs, the leads, the point lengths,
 * the scorers. Pure, over `PointEvent[]` in the order they were played;
 * nothing here reads a clock or a dictionary.
 */

export type Side = "A" | "B";

/** The running score after each point, with 0-0 in front: the worm's dots. */
export function worm(points: readonly PointEvent[]): { scoreA: number; scoreB: number }[] {
  return [{ scoreA: 0, scoreB: 0 }, ...points.map((p) => ({ scoreA: p.scoreA, scoreB: p.scoreB }))];
}

export interface RunsAndLeads {
  /** The most consecutive points one side scored. */
  longestRun: { team: Side; length: number } | null;
  /** The biggest margin either side held at any moment. */
  largestLead: { team: Side; lead: number } | null;
  /** How many times the lead passed from one side to the other (a tie in
   *  between does not count until the other side goes ahead). */
  leadChanges: number;
}

export function runsAndLeads(points: readonly PointEvent[]): RunsAndLeads {
  let longestRun: RunsAndLeads["longestRun"] = null;
  let largestLead: RunsAndLeads["largestLead"] = null;
  let leadChanges = 0;
  let run: { team: Side; length: number } | null = null;
  let leader: Side | null = null;
  for (const p of points) {
    run = run !== null && run.team === p.team ? { team: p.team, length: run.length + 1 } : { team: p.team, length: 1 };
    if (longestRun === null || run.length > longestRun.length) longestRun = run;
    const margin = p.scoreA - p.scoreB;
    if (margin !== 0) {
      const ahead: Side = margin > 0 ? "A" : "B";
      const lead = Math.abs(margin);
      if (largestLead === null || lead > largestLead.lead) largestLead = { team: ahead, lead };
      if (leader !== null && leader !== ahead) leadChanges += 1;
      leader = ahead;
    }
  }
  return { longestRun, largestLead, leadChanges };
}

/** How long each point took, from the previous point's end. The first point
 *  has no start on record, so it is `null` there. */
export function pointDurations(points: readonly PointEvent[]): (number | null)[] {
  return points.map((p, i) => {
    const prev = points[i - 1];
    if (prev === undefined) return null;
    const ms = p.at - prev.at;
    return ms >= 0 ? ms : null;
  });
}

/** From the end of the first point to the end of the last: the part of the
 *  game the record can measure. `null` with fewer than two points. */
export function gameSpanMs(points: readonly PointEvent[]): number | null {
  const first = points[0];
  const last = points[points.length - 1];
  if (first === undefined || last === undefined || first === last) return null;
  const ms = last.at - first.at;
  return ms >= 0 ? ms : null;
}

export interface ScorerLine {
  playerId: string;
  goals: number;
  assists: number;
}

/** One side's credited players, most goals first, then assists. Uncredited
 *  points are not a player. */
export function scorersOf(points: readonly PointEvent[], side: Side): ScorerLine[] {
  const tally = new Map<string, ScorerLine>();
  const bump = (id: string | null, key: "goals" | "assists"): void => {
    if (id === null) return;
    const row = tally.get(id) ?? { playerId: id, goals: 0, assists: 0 };
    row[key] += 1;
    tally.set(id, row);
  };
  for (const p of points) {
    if (p.team !== side) continue;
    bump(p.scorerId, "goals");
    bump(p.assistId, "assists");
  }
  return [...tally.values()].sort(
    (x, y) => y.goals - x.goals || y.assists - x.assists || x.playerId.localeCompare(y.playerId),
  );
}

export interface Hookup {
  scorerId: string;
  assistId: string;
  count: number;
}

/** Scorer-and-assist pairs that happened more than once, most often first. */
export function hookupsOf(points: readonly PointEvent[], side: Side): Hookup[] {
  const tally = new Map<string, Hookup>();
  for (const p of points) {
    if (p.team !== side || p.scorerId === null || p.assistId === null) continue;
    const key = `${p.scorerId}|${p.assistId}`;
    const row = tally.get(key) ?? { scorerId: p.scorerId, assistId: p.assistId, count: 0 };
    row.count += 1;
    tally.set(key, row);
  }
  return [...tally.values()]
    .filter((h) => h.count > 1)
    .sort((x, y) => y.count - x.count || x.scorerId.localeCompare(y.scorerId));
}

/**
 * The score when a side first reached half the target - the halftime rule of
 * a game to N - and which point that was. `target` is the winner's final
 * score, which is the game's target unless a cap cut it short; the number is
 * an honest reading of the record, not the clock.
 */
export function scoreAtHalf(
  points: readonly PointEvent[],
  target: number,
): { scoreA: number; scoreB: number; afterPoint: number } | null {
  if (target < 2) return null;
  const half = Math.ceil(target / 2);
  const i = points.findIndex((p) => p.scoreA >= half || p.scoreB >= half);
  if (i < 0) return null;
  const p = points[i];
  if (p === undefined) return null;
  // The last point IS the half only in a game that never had a second half.
  if (i === points.length - 1) return null;
  return { scoreA: p.scoreA, scoreB: p.scoreB, afterPoint: i + 1 };
}

/** Breaks per side, the live card's own count. */
export function breaksOf(points: readonly PointEvent[]): { A: number; B: number } {
  let A = 0;
  let B = 0;
  for (const p of points) {
    if (!isBreak(p)) continue;
    if (p.team === "A") A += 1;
    else B += 1;
  }
  return { A, B };
}
